import { ReactNode, useCallback, KeyboardEvent } from "react";

import Box from "ds/components/Box";
import useTypedContext from "hooks/useTypedContext";
import { TreeNode } from "utils/tree";

import { TreeListContext } from "../Context";
import { BaseTreeListItemProps } from "../types";
import styles from "../styles.module.css";

const TREE_LIST_INDENT = `(var(--tree-list-v2-item-padding-left) + var(--tree-list-v2-indentation-marker-wrapper-width))`;

const getTreeListGuideLeft = (ancestorLevel: number) =>
  `calc(var(--tree-list-v2-item-padding-left) + var(--tree-list-v2-indentation-marker-wrapper-width) / 2 + ${ancestorLevel} * ${TREE_LIST_INDENT})`;

type TreeListVirtualizedRowProps<T extends object> = {
  node: TreeNode<T>;
  index: number;
  start: number;
  renderItem: (option: BaseTreeListItemProps<T>) => ReactNode;
  measureElement: (element: HTMLDivElement | null) => void;
};

const TreeListVirtualizedRow = <T extends object>({
  node,
  index,
  start,
  renderItem,
  measureElement,
}: TreeListVirtualizedRowProps<T>) => {
  const {
    expandedKeys,
    renderedKeys,
    disabledKeys,
    toggleKey,
    selectedKey,
    searchQuery,
    onChange,
    onFocus,
    onKeyDown,
    selectedRef,
    focusedKey,
  } = useTypedContext(TreeListContext);

  const isExpanded = expandedKeys.has(node.id);
  const isDisabled = disabledKeys?.includes(node.id);
  const isSelected = node.id === selectedKey;
  const hasChildren = node.children.some((child) => renderedKeys.has(child.id));
  const treeListLevel = node.path.length - 1;

  const handleToggle = useCallback(() => {
    toggleKey(node.id);
  }, [node.id, toggleKey]);

  const handleClick = useCallback(() => {
    onChange?.(node.id);
  }, [node.id, onChange]);

  const handleFocus = useCallback(() => {
    onFocus(node.id);
  }, [node.id, onFocus]);

  const handleBlur = useCallback(() => {
    onFocus("");
  }, [onFocus]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      onKeyDown(event, node.id);
    },
    [node.id, onKeyDown]
  );

  return (
    <Box
      data-index={index}
      ref={measureElement}
      className={styles.virtualizedRow}
      style={{
        transform: `translateY(${start}px)`,
        paddingLeft: `calc(${treeListLevel} * var(--tree-list-indent-step))`,
      }}
      fullWidth
    >
      {treeListLevel > 0 && (
        <Box className={styles.guides} aria-hidden>
          {Array.from({ length: treeListLevel }).map((_, ancestorLevel) => (
            <span
              key={ancestorLevel}
              className={styles.guide}
              style={{ left: getTreeListGuideLeft(ancestorLevel) }}
            />
          ))}
        </Box>
      )}
      {renderItem({
        item: node.item,
        id: node.id,
        name: node.name,
        selected: isSelected,
        isExpanded: hasChildren ? isExpanded : undefined,
        isDisabled,
        searchQuery,
        innerRef: isSelected ? selectedRef : undefined,
        hasFocus: node.id === focusedKey,
        ariaLevel: node.path.length,
        onToggle: hasChildren ? handleToggle : undefined,
        onClick: handleClick,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
      })}
    </Box>
  );
};

TreeListVirtualizedRow.displayName = "DS.TreeList.VirtualizedRow";

export default TreeListVirtualizedRow;
